import Http from "../engine/Http";
import { Identifiable } from "../model/Items";
import useAuthenticationHeader from "../security/AuthenticationHeader.hook";

export const enum OrderStatus {
  Created = 'CREATED',
  InProgress = 'IN_PROGRESS',
  Complete = 'COMPLETE'
}

export interface FetchedOrder
extends Identifiable {
  customer: Identifiable,
  completionDate: string|undefined;
}

export interface Order {
  number?: string;
  creationDate: string;
  completionDate: string|undefined;
  priceList: Identifiable;
  status: OrderStatus;
}

export interface OrderedKit {
  kit: Identifiable;
  quantityDelivered: number;
}

export interface OrderedForm {
  form: Identifiable;
  quantityDelivered: number;
}

export interface OrderedShape {
  shape: Identifiable;
  quantityDelivered: number;
}

export interface OrderedKitForm
extends ItemOrderedKit {
  form: Identifiable;
}

export interface OrderedKitShape
extends ItemOrderedKit {
  shape: Identifiable;
}

export interface ItemOrderedKit {
  kit: {
    name: string;
  }
  order: {
    number: string;
    status: OrderStatus;
  }
}

const useOrderApi = () => {
  const authenticationHeader = useAuthenticationHeader();

  const fetchOrderElements = (customerId: number, orderId: number, orderItemResource: 'forms'|'shapes'|'kits') => Http
    .get(`/api/customers/${customerId}/orders/${orderId}/${orderItemResource}`, [ authenticationHeader ]);

  const fetchOrders = () : Promise<FetchedOrder[]> => Http.get(`/api/orders`, [ authenticationHeader ]);

  const fetchOrderForms = (customerId: number, orderId: number) : Promise<OrderedForm[]> =>
    fetchOrderElements(customerId, orderId, 'forms');

  const fetchOrderShapes = (customerId: number, orderId: number) : Promise<OrderedShape[]> =>
    fetchOrderElements(customerId, orderId, 'shapes');

  const fetchOrderKits = (customerId: number, orderId: number) : Promise<OrderedKit[]> =>
    fetchOrderElements(customerId, orderId, 'kits');

  const fetchFormActiveOrders = (kitId: number) : Promise<Order[]> => Http.get(`/api/orders?formId=${kitId}`, [ authenticationHeader ]);

  const fetchKitActiveOrders = (kitId: number) : Promise<Order[]> => Http.get(`/api/orders?kitId=${kitId}`, [ authenticationHeader ]);

  const fetchActiveOrderedForms = (customerId: number) : Promise<OrderedForm[]> => Http
    .get(`/api/customers/${customerId}/ordered-forms?orderStatus=${OrderStatus.Created},${OrderStatus.InProgress}`, [ authenticationHeader ]);

  const fetchActiveOrderedShapes = (customerId: number) : Promise<OrderedShape[]> => Http
    .get(`/api/customers/${customerId}/ordered-shapes?orderStatus=${OrderStatus.Created},${OrderStatus.InProgress}`, [ authenticationHeader ]);

  const fetchActiveOrderedKits = (customerId: number) : Promise<OrderedKit[]> => Http
    .get(`/api/customers/${customerId}/ordered-kits?orderStatus=${OrderStatus.Created},${OrderStatus.InProgress}`, [ authenticationHeader ]);

  const fetchOrderedKitForms = (customerId: number) : Promise<OrderedKitForm[]> => Http
    .get(`/api/customers/${customerId}/ordered-kit-forms?orderStatus=${OrderStatus.Created},${OrderStatus.InProgress},${OrderStatus.Complete}`, [ authenticationHeader ]);

  const fetchOrderedKitShapes = (customerId: number) : Promise<OrderedKitShape[]> => Http
    .get(`/api/customers/${customerId}/ordered-kit-shapes?orderStatus=${OrderStatus.Created},${OrderStatus.InProgress},${OrderStatus.Complete}`, [ authenticationHeader ]);

    return {
      fetchOrders,
      fetchOrderForms,
      fetchOrderShapes,
      fetchOrderKits,
      fetchFormActiveOrders,
      fetchKitActiveOrders,
      fetchActiveOrderedForms,
      fetchActiveOrderedShapes,
      fetchActiveOrderedKits,
      fetchOrderedKitForms,
      fetchOrderedKitShapes
    };
};

export default useOrderApi;